import React, { useState } from 'react';
import { Modal, Banner, FormField } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  account_scope: string;
  user_type: string;
  source_user_id: string;
}

type FormErrors = Partial<Record<keyof FormData, string>>;

const ROLE_HINTS: Record<string, string> = {
  admin: 'Full access to all operations',
  super: 'Can view data within the selected account scope',
  normal: 'Standard user access',
};

const EMPTY: FormData = {
  email: '', first_name: '', last_name: '', role: 'normal', account_scope: 'all', user_type: 'RM', source_user_id: '',
};

const UserCreateDialog: React.FC<UserCreateDialogProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<FormData>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateEmail = (v: string) => {
    if (!v) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Invalid email format';
  };

  const validate = () => {
    const e: FormErrors = {};
    const emailErr = validateEmail(formData.email);
    if (emailErr) e.email = emailErr;
    if (!formData.first_name.trim()) e.first_name = 'First name is required';
    if (!formData.last_name.trim()) e.last_name = 'Last name is required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const reset = () => { setFormData(EMPTY); setErrors({}); setApiError(null); };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setApiError(null);
    try {
      const payload: any = {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        account_scope: formData.role === 'super' ? formData.account_scope : 'all',
        user_type: formData.user_type,
      };
      if (formData.source_user_id.trim()) payload.source_user_id = formData.source_user_id.trim();
      await apiService.createUser(payload);
      reset(); onSuccess(); onClose();
    } catch (err: any) {
      setApiError(err.message || 'Failed to create user');
    } finally { setLoading(false); }
  };

  const handleClose = () => { if (!loading) { reset(); onClose(); } };

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create New User"
      size="md"
      footer={
        <>
          <LCBtn variant="outline" size="sm" onClick={handleClose} disabled={loading}>Cancel</LCBtn>
          <LCBtn variant="primary" size="sm" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating…' : 'Create User'}
          </LCBtn>
        </>
      }
    >
      {apiError && <Banner variant="error" onClose={() => setApiError(null)} className="mb-4">{apiError}</Banner>}

      <div className="flex flex-col gap-3.5">
        <FormField label="Email" required error={errors.email} hint="User will log in with this email">
          <input className={CLS.input} type="email" value={formData.email} onChange={set('email')} disabled={loading} />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="First Name" required error={errors.first_name}>
            <input className={CLS.input} value={formData.first_name} onChange={set('first_name')} disabled={loading} />
          </FormField>
          <FormField label="Last Name" required error={errors.last_name}>
            <input className={CLS.input} value={formData.last_name} onChange={set('last_name')} disabled={loading} />
          </FormField>
        </div>

        <FormField label="Role" required hint={ROLE_HINTS[formData.role]}>
          <select className={CLS.input} value={formData.role} onChange={set('role')} disabled={loading}>
            <option value="normal">Normal</option>
            <option value="super">Super (Read-only)</option>
            <option value="admin">Admin (Full Access)</option>
          </select>
        </FormField>

        {formData.role === 'super' && (
          <FormField label="Account scope" required hint="Applies to all account views, totals and alert emails.">
            <select aria-label="Account scope" className={CLS.input} value={formData.account_scope} onChange={set('account_scope')} disabled={loading}>
              <option value="all">All accounts</option>
              <option value="avpu">AVPU only</option>
              <option value="lcp">LCP only</option>
            </select>
          </FormField>
        )}

        <FormField label="User Type" required hint="Determines portfolio access permissions">
          <select className={CLS.input} value={formData.user_type} onChange={set('user_type')} disabled={loading}>
            <option value="RM">RM (Relationship Manager)</option>
            <option value="IC">IC (Investment Counselor)</option>
            <option value="ARM">ARM (Assistant RM)</option>
            <option value="ADMIN">ADMIN</option>
            <option value="SUPER">SUPER</option>
            <option value="COMPLIANCE">COMPLIANCE</option>
            <option value="OPS">OPS</option>
          </select>
        </FormField>

        <FormField label="Source User ID" hint="Links to upstream system user_access_info.user_id">
          <input className={CLS.input} value={formData.source_user_id} onChange={set('source_user_id')} disabled={loading} />
        </FormField>
      </div>
    </Modal>
  );
};

export default UserCreateDialog;
